import React, { FC, ReactNode, useCallback, useEffect, useState } from "react"
import ReactDOM from "react-dom"

import { generateConditionalClassName, isClientSide } from "@utils"

import Icon, { ICON_TYPES } from "../Icon/Icon"

import "./Modal.scss"

export type ModalProps = {
  children: ReactNode | ((closeModal: () => void) => ReactNode),
  hideCloseButton?: boolean,
  onClose?: () => void,
  onOpen?: () => void,
  renderWithoutBox?: boolean,
  useInsetCloseButton?: boolean,
  useNoPaddingBottom?: boolean,
  width?: string,
}

const Modal: FC<ModalProps> = ({
  children,
  hideCloseButton,
  onClose,
  onOpen = null,
  renderWithoutBox,
  useInsetCloseButton,
  useNoPaddingBottom,
  width,
}) => {
  const [isClosing, setIsClosing] = useState<boolean>(false)
  const [modalEl, setModalEl] = useState<HTMLDivElement | null>(null)

  const closeModal = useCallback(() => {
    if (isClosing) return

    document.body.style.overflow = "auto"
    setIsClosing(true)
  }, [onClose])

  useEffect(() => {
    if (!isClientSide()) return
    if (onOpen) onOpen()
  }, [])

  useEffect(() => {
    if (!isClientSide()) return

    const onKeyUp = (e: KeyboardEvent) => {
      // If the `esc` key is pressed:
      if (e.which === 27) closeModal()
    }

    const _modalEl = document.createElement("div")

    setModalEl(_modalEl)
    document.body.append(_modalEl)

    document.body.style.overflow = "hidden"

    window.addEventListener("keyup", onKeyUp)

    setTimeout(() => {
      const inputEls = _modalEl.getElementsByTagName("input")
      if (inputEls.length) {
        inputEls[0].focus()
      } else {
        const closeButtonEl: HTMLButtonElement | null = _modalEl.querySelector(".close-button")
        if (closeButtonEl) {
          closeButtonEl.focus()
        }
      }
    }, 500)

    return () => {
      document.body.removeChild(_modalEl)
      document.body.style.overflow = "auto"
      window.removeEventListener("keyup", onKeyUp)
      setModalEl(null)
    }
  }, [])

  return modalEl ?
    ReactDOM.createPortal(
      <div
        className={generateConditionalClassName({
          "Modal--Overlay": true,
          "is-closing": isClosing,
        })}
        onAnimationEnd={() => {
          if (!isClosing) return
          if (onClose) onClose()
        }}
      >
        <button
          className="Modal--OverlayTransparencyButton"
          onClick={closeModal}
        />
        {!useInsetCloseButton && !hideCloseButton && (
          <button
            className="Modal--CloseButton"
            title="Close"
            onClick={closeModal}
          >
            <Icon
              color="var(--color--basics-white)"
              size="32px"
              type={ICON_TYPES["close"]}
            />
          </button>
        )}
        {renderWithoutBox ? (
          <div className="Modal--Children">
            {typeof children === "function" ? children(closeModal) : children}
          </div>
        ) : (
          <div
            className="Modal--Card"
            style={{
              paddingBottom: useNoPaddingBottom ? "0" : undefined,
              width,
            }}
          >
            {useInsetCloseButton && (
              <div
                className="Modal--CloseButton inset"
                title="Close"
                onClick={closeModal}
              >
                <Icon
                  color="var(--color--text-light)"
                  size="var(--spacing--S)"
                  type={ICON_TYPES["close"]}
                />
              </div>
            )}
            {typeof children === "function" ? children(closeModal) : children}
          </div>
        )}
      </div>
      , modalEl)
    : null
}

export default Modal
